import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Plus, Save, Share2, Trash2, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import DashboardShell from "../components/DashboardShell";
import { api, formatErr } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { DeliveryLocationPicker } from "../components/DeliveryLocationPicker";
import DeliveryRadiusMap from "../components/DeliveryRadiusMap";

const DAYS = [
  ["sat", "السبت"],
  ["sun", "الأحد"],
  ["mon", "الإثنين"],
  ["tue", "الثلاثاء"],
  ["wed", "الأربعاء"],
  ["thu", "الخميس"],
  ["fri", "الجمعة"],
];

export default function BranchEdit() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const branchContext = useBranch();
  const [branch, setBranch] = useState(null);
  const [initialBranch, setInitialBranch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [draftLocation, setDraftLocation] = useState(null);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  useEffect(() => {
    api.get(`/branches/${branchId}`)
      .then(({ data }) => {
        setBranch(data);
        setInitialBranch(cloneBranch(data));
      })
      .catch((error) => {
        toast.error(formatErr(error));
        navigate("/dashboard/settings", { replace: true });
      });
  }, [branchId, navigate]);

  const update = (key, value) => {
    setBranch((current) => ({ ...current, [key]: value }));
  };

  const save = async () => {
    if (!branch.name?.trim()) {
      toast.error("اسم الفرع مطلوب");
      return;
    }
    const latitude = optionalNumber(branch.latitude);
    const longitude = optionalNumber(branch.longitude);
    if ((latitude === null) !== (longitude === null)) {
      toast.error("يرجى إدخال إحداثيات الموقع كاملة");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: branch.name.trim(),
        address: branch.address || "",
        phone: branch.phone || "",
        hours: branch.hours || {},
        orders_accepting_now: branch.orders_accepting_now !== false,
        pickup_prep_time_minutes: Number(branch.pickup_prep_time_minutes || 0),
        delivery_prep_time_minutes: Number(branch.delivery_prep_time_minutes || 0),
        car_pickup_enabled: !!branch.car_pickup_enabled,
        delivery_enabled: !!branch.delivery_enabled,
        delivery_fee: Number(branch.delivery_fee || 0),
        max_delivery_km: Number(branch.max_delivery_km || 0),
      };
      if (latitude !== null && longitude !== null) {
        payload.latitude = latitude;
        payload.longitude = longitude;
      }
      const { data } = await api.patch(`/branches/${branchId}`, payload);
      setBranch(data);
      setInitialBranch(cloneBranch(data));
      await branchContext?.reload();
      toast.success("تم حفظ إعدادات الفرع");
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setBusy(false);
    }
  };

  const openLocationModal = () => {
    setDraftLocation({
      latitude: optionalNumber(branch.latitude),
      longitude: optionalNumber(branch.longitude),
      address: branch.address || "",
    });
    setLocationModalOpen(true);
  };

  const saveLocation = async () => {
    const latitude = optionalNumber(draftLocation?.latitude);
    const longitude = optionalNumber(draftLocation?.longitude);
    if (latitude === null || longitude === null) {
      toast.error("حدد موقع الفرع من الخريطة");
      return;
    }
    setBusy(true);
    try {
      const payload = { latitude, longitude };
      if (draftLocation?.address) payload.address = draftLocation.address;
      const { data } = await api.patch(`/branches/${branchId}`, payload);
      const savedLocation = {
        latitude: data.latitude,
        longitude: data.longitude,
      };
      if (data.address || draftLocation?.address) {
        savedLocation.address = data.address || draftLocation.address;
      }
      setBranch((current) => ({
        ...current,
        ...savedLocation,
      }));
      setInitialBranch((current) => ({
        ...current,
        ...savedLocation,
      }));
      await branchContext?.reload();
      setLocationModalOpen(false);
      toast.success("تم حفظ موقع الفرع");
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setBusy(false);
    }
  };

  const isDirty = (
    branch
    && initialBranch
    && !areBranchesEqual(branch, initialBranch)
  );

  const requestClose = () => {
    if (busy) {
      return;
    }
    if (isDirty) {
      setShowUnsavedConfirm(true);
      return;
    }
    navigate("/dashboard/settings");
  };

  const leaveWithoutSaving = () => {
    setShowUnsavedConfirm(false);
    navigate("/dashboard/settings");
  };

  if (!branch) {
    return (
      <DashboardShell title="إعدادات الفرع">
        <div data-testid="branch-edit-loading">جاري التحميل…</div>
      </DashboardShell>
    );
  }

  const branchUrl = (
    `${window.location.origin}/r/${branch.restaurant_slug}/${branch.branch_code}`
  );
  const posterUrl = (
    `/dashboard/qr-poster?slug=${branch.restaurant_slug}&branch_id=${branch.id}`
  );
  const copy = async () => {
    await navigator.clipboard.writeText(branchUrl);
    toast.success("تم نسخ رابط الفرع");
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: branch.name, url: branchUrl });
      } catch {
        return;
      }
    } else {
      await copy();
    }
  };
  const downloadQr = () => {
    const svg = document.getElementById("branch-qr-svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 600;
    const image = new Image();
    image.onload = () => {
      const context = canvas.getContext("2d");
      context.fillStyle = "#fff";
      context.fillRect(0, 0, 600, 600);
      context.drawImage(image, 0, 0, 600, 600);
      const anchor = document.createElement("a");
      anchor.href = canvas.toDataURL("image/png");
      anchor.download = `qr-branch-${branch.id}.png`;
      anchor.click();
    };
    image.src = (
      `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`
    );
  };

  return (
    <DashboardShell title="إعدادات الفرع" restaurantName={branch.name}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <button
          onClick={requestClose}
          className={
            "grid h-10 w-10 place-items-center rounded-full border border-orange-100 "
            + "bg-white text-orange-700"
          }
          data-testid="branch-edit-back"
          aria-label="إغلاق إعدادات الفرع"
          title="إغلاق"
        >
          <X size={18}/>
        </button>
        <button
          onClick={save}
          disabled={busy}
          className={
            "pill brand-grad inline-flex items-center gap-1 px-4 py-2 text-sm font-bold "
            + "text-white disabled:opacity-60"
          }
          data-testid="branch-save-settings"
        >
          <Save size={15}/>{busy ? "جاري الحفظ…" : "حفظ"}
        </button>
      </div>

      <section
        className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
        data-testid="branch-qr-section"
      >
        <h2 className="mb-1 font-extrabold">رمز QR للفرع</h2>
        <p className="text-xs text-gray-500">
          يبقى الرمز مرتبطًا بهذا الفرع حتى عند تغيير اسمه.
        </p>
        <div
          className="mt-3 break-all rounded-xl bg-gray-50 px-3 py-2 text-sm num-badge"
          data-testid="branch-qr-url"
        >
          {branchUrl}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={copy}
            className={
              "pill border border-orange-200 bg-white px-3 py-2 text-xs font-bold "
              + "text-orange-700"
            }
            data-testid="branch-copy-link"
          >
            <Copy size={14}/> نسخ الرابط
          </button>
          <button
            onClick={share}
            className={
              "pill border border-orange-200 bg-white px-3 py-2 text-xs font-bold "
              + "text-orange-700"
            }
            data-testid="branch-share-link"
          >
            <Share2 size={14}/> مشاركة
          </button>
          <button
            onClick={downloadQr}
            className={
              "pill border border-orange-200 bg-white px-3 py-2 text-xs font-bold "
              + "text-orange-700"
            }
            data-testid="branch-download-qr"
          >
            <Download size={14}/> تحميل QR
          </button>
          <Link
            to={posterUrl}
            className="pill brand-grad px-3 py-2 text-xs font-bold text-white"
            data-testid="branch-print-poster"
          >
            ملصق A4
          </Link>
        </div>
        <div className="mt-4 text-center">
          <div className="inline-block rounded-2xl border border-orange-100 bg-white p-3">
            <QRCodeSVG
              id="branch-qr-svg"
              value={branchUrl}
              size={180}
              level="M"
              bgColor="#ffffff"
              fgColor="#111827"
              data-testid="branch-qr-code"
            />
          </div>
        </div>
      </section>

      <section
        className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
        data-testid="branch-information-section"
      >
        <h2 className="mb-3 font-extrabold">معلومات الفرع</h2>
        <div className="space-y-3">
          <Field
            label="اسم الفرع"
            value={branch.name || ""}
            onChange={(value) => update("name", value)}
            testid="branch-edit-name"
          />
          <Field
            label="رقم الجوال"
            value={branch.phone || ""}
            onChange={(value) => update("phone", value)}
            testid="branch-edit-phone"
          />
          <Field
            label="العنوان"
            value={branch.address || ""}
            onChange={(value) => update("address", value)}
            testid="branch-edit-address"
          />
          <div
            className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3"
            data-testid="branch-location-section"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold text-gray-700">موقع الفرع</div>
                <div className="mt-1 text-xs text-gray-500" data-testid="branch-location-summary">
                  {formatLocation(branch.address, branch.latitude, branch.longitude)}
                </div>
              </div>
              <button
                type="button"
                onClick={openLocationModal}
                className={
                  "pill border border-orange-200 bg-white px-3 py-2 text-xs font-bold "
                  + "text-orange-700"
                }
                data-testid="branch-edit-location-button"
              >
                تعديل الموقع
              </button>
            </div>
          </div>
        </div>
      </section>

      <section
        className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
        data-testid="branch-operations-section"
      >
        <h2 className="mb-3 font-extrabold">التشغيل</h2>
        <ToggleRow
          label="استقبال الطلبات"
          checked={branch.orders_accepting_now !== false}
          onChange={(value) => update("orders_accepting_now", value)}
          testid="branch-orders-toggle"
        />
        <Field
          label="مدة تجهيز الاستلام (دقيقة)"
          type="number"
          value={branch.pickup_prep_time_minutes ?? 0}
          onChange={(value) => update("pickup_prep_time_minutes", value)}
          testid="branch-edit-pickup-prep-time"
        />
        <Field
          label="مدة تجهيز التوصيل (دقيقة)"
          type="number"
          value={branch.delivery_prep_time_minutes ?? 0}
          onChange={(value) => update("delivery_prep_time_minutes", value)}
          testid="branch-edit-delivery-prep-time"
        />
      </section>

      <section
        className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
        data-testid="branch-pickup-section"
      >
        <h2 className="mb-3 font-extrabold">الاستلام</h2>
        <div
          className="flex items-center justify-between border-b py-2"
          data-testid="branch-store-pickup-status"
        >
          <span className="text-sm font-medium">استلام من الفرع</span>
          <span className="pill bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">
            مفعّل دائمًا
          </span>
        </div>
        <ToggleRow
          label="استلام عند السيارة"
          checked={!!branch.car_pickup_enabled}
          onChange={(value) => update("car_pickup_enabled", value)}
          testid="branch-car-pickup-toggle"
        />
      </section>

      <section
        className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
        data-testid="branch-delivery-section"
      >
        <h2 className="mb-3 font-extrabold">التوصيل</h2>
        <ToggleRow
          label="تفعيل التوصيل"
          checked={!!branch.delivery_enabled}
          onChange={(value) => update("delivery_enabled", value)}
          testid="branch-delivery-toggle"
        />
        {branch.delivery_enabled && (
          <div className="mt-3 space-y-3">
            <Field
              label="رسوم التوصيل"
              type="number"
              value={branch.delivery_fee ?? 0}
              onChange={(value) => update("delivery_fee", value)}
              testid="branch-edit-delivery-fee"
            />
            <DeliveryRadiusMap
              latitude={branch.latitude}
              longitude={branch.longitude}
              maxDeliveryKm={branch.max_delivery_km}
            />
            <Field
              label="أقصى مسافة للتوصيل (كم)"
              type="number"
              value={branch.max_delivery_km ?? 0}
              onChange={(value) => update("max_delivery_km", value)}
              testid="branch-edit-max-delivery-km"
            />
          </div>
        )}
      </section>

      <BranchHoursEditor
        hours={branch.hours || {}}
        onChange={(hours) => update("hours", hours)}
      />
      {locationModalOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/45 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) setLocationModalOpen(false);
          }}
          data-testid="branch-location-modal"
        >
          <div
            className="mx-auto mt-8 w-full max-w-lg rounded-3xl bg-white p-5 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label="تعديل موقع الفرع"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="text-lg font-extrabold">تعديل موقع الفرع</div>
              <button
                type="button"
                onClick={() => setLocationModalOpen(false)}
                disabled={busy}
                className="grid h-9 w-9 place-items-center rounded-xl bg-gray-100 text-gray-600"
                aria-label="إغلاق"
                data-testid="branch-location-modal-close"
              >
                <X size={18}/>
              </button>
            </div>
            <DeliveryLocationPicker
              latitude={optionalNumber(draftLocation?.latitude)}
              longitude={optionalNumber(draftLocation?.longitude)}
              onLocationChange={({ latitude, longitude }) => {
                setDraftLocation((current) => ({ ...current, latitude, longitude }));
              }}
              onAddressDetected={(address) => {
                if (address) {
                  setDraftLocation((current) => ({ ...current, address }));
                }
              }}
              title="موقع الفرع"
              instruction="حرّك الخريطة حتى يصبح الدبوس فوق موقع الفرع، أو استخدم موقعك الحالي."
            />
            <button
              type="button"
              onClick={saveLocation}
              disabled={busy}
              className="mt-4 w-full pill brand-grad py-3 font-bold text-white disabled:opacity-60"
              data-testid="branch-location-modal-save"
            >
              {busy ? "جاري الحفظ…" : "حفظ الموقع"}
            </button>
          </div>
        </div>
      )}
      {showUnsavedConfirm && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4"
          data-testid="branch-unsaved-changes-backdrop"
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-6 text-right shadow-2xl rise-in"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="branch-unsaved-changes-title"
            data-testid="branch-unsaved-changes-modal"
          >
            <h2
              id="branch-unsaved-changes-title"
              className="text-lg font-extrabold text-gray-900"
              data-testid="branch-unsaved-changes-title"
            >
              لديك تعديلات غير محفوظة، هل تريد حفظها؟
            </h2>
            <p
              className="mt-2 text-sm leading-6 text-gray-600"
              data-testid="branch-unsaved-changes-description"
            >
              اختر «نعم» للبقاء في الصفحة ثم استخدم زر الحفظ، أو «لا» للخروج دون حفظ.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={leaveWithoutSaving}
                className="pill bg-red-600 px-4 py-3 font-bold text-white hover:bg-red-700"
                data-testid="branch-unsaved-changes-no"
              >
                لا
              </button>
              <button
                type="button"
                onClick={() => setShowUnsavedConfirm(false)}
                className="pill border border-orange-200 bg-white px-4 py-3 font-bold text-orange-700"
                data-testid="branch-unsaved-changes-yes"
              >
                نعم
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function BranchHoursEditor({ hours, onChange }) {
  const updateDay = (day, patch) => {
    onChange({ ...hours, [day]: { ...(hours[day] || {}), ...patch } });
  };
  const addShift = (day) => {
    const current = hours[day] || { enabled: true, shifts: [] };
    updateDay(day, {
      enabled: true,
      shifts: [
        ...(current.shifts || []),
        { opening_time: "12:00", closing_time: "22:00" },
      ],
    });
  };
  const updateShift = (day, index, patch) => {
    const current = hours[day] || { enabled: true, shifts: [] };
    const shifts = [...(current.shifts || [])];
    shifts[index] = { ...shifts[index], ...patch };
    updateDay(day, { shifts });
  };
  const removeShift = (day, index) => {
    const current = hours[day] || { enabled: true, shifts: [] };
    updateDay(day, {
      shifts: current.shifts.filter((_, currentIndex) => currentIndex !== index),
    });
  };

  return (
    <section
      className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
      data-testid="branch-hours-section"
    >
      <h2 className="mb-1 font-extrabold">ساعات العمل</h2>
      <p className="mb-3 text-xs text-gray-500">
        الإغلاق قبل الفتح يعني أن الفترة تمتد إلى اليوم التالي.
      </p>
      <div className="space-y-3">
        {DAYS.map(([key, name]) => {
          const day = hours[key] || {
            enabled: true,
            shifts: [{ opening_time: "10:00", closing_time: "23:59" }],
          };
          return (
            <div
              key={key}
              className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3"
              data-testid={`branch-hours-${key}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={day.enabled !== false}
                    onChange={(event) => updateDay(key, { enabled: event.target.checked })}
                    className="h-4 w-4 accent-orange-600"
                    data-testid={`branch-hours-enable-${key}`}
                  />
                  <span className="font-bold">{name}</span>
                </label>
                <button
                  onClick={() => addShift(key)}
                  className={
                    "pill inline-flex items-center gap-1 border border-orange-200 bg-white "
                    + "px-3 py-1 text-xs font-bold text-orange-700"
                  }
                  data-testid={`branch-hours-add-shift-${key}`}
                >
                  <Plus size={12}/> فترة
                </button>
              </div>
              {day.enabled !== false && (
                <div className="space-y-2">
                  {(day.shifts || []).map((shift, index) => (
                    <div
                      key={index}
                      className={
                        "grid grid-cols-2 gap-2 rounded-xl border border-orange-100 bg-white "
                        + "p-2"
                      }
                      data-testid={`branch-shift-${key}-${index}`}
                    >
                      <TimeField
                        label="وقت الفتح"
                        value={shift.opening_time ?? shift.open ?? ""}
                        onChange={(value) => updateShift(key, index, { opening_time: value })}
                        testid={`branch-shift-open-${key}-${index}`}
                      />
                      <TimeField
                        label="وقت الإغلاق"
                        value={shift.closing_time ?? shift.close ?? ""}
                        onChange={(value) => updateShift(key, index, { closing_time: value })}
                        testid={`branch-shift-close-${key}-${index}`}
                      />
                      <button
                        onClick={() => removeShift(key, index)}
                        className={
                          "col-span-2 pill inline-flex items-center justify-center gap-1 border "
                          + "border-red-100 bg-red-50 py-1.5 text-xs font-bold text-red-600"
                        }
                        data-testid={`branch-shift-delete-${key}-${index}`}
                      >
                        <Trash2 size={12}/> حذف هذه الفترة
                      </button>
                    </div>
                  ))}
                  {(day.shifts || []).length === 0 && (
                    <div className="text-xs text-gray-500" data-testid={`branch-no-shifts-${key}`}>
                      لا فترات — اليوم مغلق
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testid}
        className={
          "mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 "
          + "outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        }
      />
    </label>
  );
}

function TimeField({ label, value, onChange, testid }) {
  return (
    <Field
      label={label}
      value={value}
      onChange={onChange}
      testid={testid}
      type="time"
    />
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between border-b py-2 last:border-0">
      <span className="text-sm font-medium">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 accent-orange-600"
        data-testid={testid}
      />
    </label>
  );
}

function optionalNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function cloneBranch(value) {
  return JSON.parse(JSON.stringify(value));
}

function areBranchesEqual(current, baseline) {
  return JSON.stringify(current) === JSON.stringify(baseline);
}

function formatLocation(address, latitude, longitude) {
  if (address?.trim()) return address;
  const savedLatitude = optionalNumber(latitude);
  const savedLongitude = optionalNumber(longitude);
  if (savedLatitude === null || savedLongitude === null) {
    return "لم يتم تحديد الموقع بعد";
  }
  return "تم تحديد الموقع";
}